import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChatCircleText, Copy, Check, Paperclip, X } from "@phosphor-icons/react";
import { submitSupportTicket } from "@/lib/api";

const ISSUE_TYPES = [
  { value: "bug", label: "Bug — something is broken" },
  { value: "feature", label: "Feature request" },
  { value: "billing", label: "Billing / account" },
  { value: "data", label: "Data concern or recovery" },
  { value: "other", label: "Other" },
];

// Floating, always-on badge. When clicked it opens a ticket dialog.
// Submits to POST /api/support/tickets — backend persists to MongoDB,
// mirrors to Firestore (`support_tickets` collection), and emails support.
export default function HaveAQuestionBadge({ projectId, compact = false, defaultIssueType }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-50 hidden md:flex items-center gap-2 h-11 px-4 rounded-full shadow-[0_8px_24px_rgba(36,22,35,0.25)] transition-all hover:scale-[1.02] active:scale-100 ${
          compact ? "bg-slate-950 text-neon hover:bg-ink" : "btn-neon hover:shadow-[0_12px_28px_rgba(199,239,0,0.35)]"
        }`}
        data-testid="have-a-question-btn"
        title="Have a question? Get in touch."
      >
        <ChatCircleText size={16} weight={compact ? "bold" : "fill"} />
        <span className="text-sm font-semibold">Have a question?</span>
      </button>

      <SupportTicketDialog
        open={open}
        onOpenChange={setOpen}
        projectId={projectId}
        defaultIssueType={defaultIssueType}
      />
    </>
  );
}

export function SupportTicketDialog({ open, onOpenChange, projectId, defaultIssueType }) {
  const [issueType, setIssueType] = useState(defaultIssueType || "bug");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null); // { dataUrl, name, size }
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setIssueType(defaultIssueType || "bug");
    setDescription("");
    setImage(null);
    setCopied(false);
  };

  const copyProjectId = async () => {
    if (!projectId) return;
    try {
      await navigator.clipboard.writeText(projectId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      toast.error("Only .jpg and .png images are accepted");
      return;
    }
    try {
      const compressed = await compressImage(file, { maxBytes: 600 * 1024 });
      setImage(compressed);
    } catch (err) {
      toast.error("Couldn't compress the image — try a smaller file");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        issue_type: issueType,
        description: description.trim(),
        project_id: projectId || null,
        image_data_url: image?.dataUrl || null,
        image_name: image?.name || null,
        image_size: image?.size || null,
      };
      const res = await submitSupportTicket(payload);
      if (res?.email_sent) {
        toast.success("Ticket received — we'll follow up within one business day.");
      } else {
        toast.success("Ticket received. (Email delivery delayed — we still got it.)");
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send the ticket — please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-md max-w-lg" data-testid="support-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            Have a question?
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            We answer within one business day. Share as much detail as you can.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-1">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Issue Type
            </Label>
            <Select value={issueType} onValueChange={setIssueType}>
              <SelectTrigger
                className="h-10 mt-1.5 rounded-md border-slate-300 text-sm focus:ring-0 focus:border-slate-950"
                data-testid="ticket-issue-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                {ISSUE_TYPES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Description
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="What's happening? Steps to reproduce, what you expected, what actually happened…"
              className="mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-sm resize-none"
              data-testid="ticket-description"
            />
          </div>

          {projectId && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                Project Reference
              </Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  readOnly
                  value={projectId}
                  className="h-9 rounded-md border-slate-300 font-mono-ibm text-xs bg-slate-50 focus-visible:ring-0"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyProjectId}
                  className="h-9 rounded-md border-slate-300 text-xs shrink-0"
                  data-testid="ticket-copy-project-id"
                >
                  {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                  <span className="ml-1.5">{copied ? "Copied" : "Click to copy"}</span>
                </Button>
              </div>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Attachment <span className="normal-case tracking-normal text-slate-400">(optional, .jpg or .png, auto-compressed to ≤ 600kb)</span>
            </Label>
            {image ? (
              <div className="mt-2 flex items-center gap-3 p-2 border border-slate-200 rounded-md bg-slate-50">
                <img src={image.dataUrl} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-950 truncate">{image.name}</div>
                  <div className="text-[10px] font-mono-ibm text-slate-500">
                    {(image.size / 1024).toFixed(0)} kb · compressed
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="h-7 w-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"
                  data-testid="ticket-image-remove"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ) : (
              <label
                className="mt-1.5 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 hover:border-slate-950 cursor-pointer text-xs text-slate-600 hover:text-slate-950"
                data-testid="ticket-image-upload"
              >
                <Paperclip size={12} weight="bold" />
                Attach a screenshot
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={onFilePicked}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-md border-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !description.trim()}
              className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="ticket-submit"
            >
              {submitting ? "Sending…" : "Send ticket"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Canvas-based image compression. Scales down iteratively until under maxBytes.
// Returns { dataUrl, name, size (bytes) }.
async function compressImage(file, { maxBytes = 600 * 1024 } = {}) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    let quality = 0.9;
    let width = img.width;
    let height = img.height;
    const maxSide = 1600;
    if (Math.max(width, height) > maxSide) {
      const scale = maxSide / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    let dataUrl = await toJpegDataUrl(img, width, height, quality);
    while (approxBytes(dataUrl) > maxBytes && quality > 0.3) {
      quality -= 0.1;
      dataUrl = await toJpegDataUrl(img, width, height, quality);
    }
    // Final fallback: progressive resize
    while (approxBytes(dataUrl) > maxBytes && width > 400) {
      width = Math.round(width * 0.85);
      height = Math.round(height * 0.85);
      dataUrl = await toJpegDataUrl(img, width, height, quality);
    }
    return { dataUrl, name: file.name.replace(/\.(png|jpg|jpeg)$/i, ".jpg"), size: approxBytes(dataUrl) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function toJpegDataUrl(img, w, h, q) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    resolve(canvas.toDataURL("image/jpeg", q));
  });
}

function approxBytes(dataUrl) {
  // base64 → bytes
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return dataUrl.length;
  const b64 = dataUrl.slice(comma + 1);
  return Math.floor(b64.length * 0.75);
}
